import { React, useState, useEffect, Toast, CustomModal, Icon, useUser } from "../../utils/commonImports";
import { Button, Collapse, Alert } from 'antd';
import { Container, Spinner } from "reactstrap";
import { _api } from '../../auth/Api';
import { getPageRights } from "../../utils/accessPages";

const Faqs = () => {
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [faq, setFaq] = useState({});
  const [allFaqs, setAllFaqs] = useState([]);
  const { loggedInUser } = useUser();
  const userPages = loggedInUser?.roles?.[0]?.pages || [];
  const rights = getPageRights(userPages, 9);
  const [faqItems, setFaqItems] = useState([
    {
      subCategory: '',
      qaPairs: [{ question: "", answer: "" }]
    }
  ]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalFaqs, setOriginalFaqs] = useState([]);

  useEffect(() => {
    _getFAQs();
  }, []);

  const _getFAQs = async () => {
    try {
      await _api("GET", "AppSetting/GetAppSettings?code=AP-010", null, (res) => {
        if (res.isSuccess) {
          setFaq(res.data)
          const parsedFaqs = JSON.parse(res.data?.value) ?? [];
          setAllFaqs(parsedFaqs);
          setOriginalFaqs(parsedFaqs);
        }
      })
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const changesExist = JSON.stringify(originalFaqs) !== JSON.stringify(allFaqs);
    setHasUnsavedChanges(changesExist);
  }, [allFaqs, originalFaqs]);

  const handleSave = () => {
    const newFaq = {
      name: selectedCategory,
      subCat: faqItems.reduce((acc, item) => {
        if (!item.subCategory) return acc;
        const existingSubCat = acc.find(sub => sub.name === item.subCategory);

        if (existingSubCat) {
          existingSubCat.questions.push(
            ...item.qaPairs.map(qa => ({
              Q: qa.question,
              answer: qa.answer
            }))
          );
        } else {
          acc.push({
            name: item.subCategory,
            questions: item.qaPairs.map(qa => ({
              Q: qa.question,
              answer: qa.answer
            }))
          });
        }
        return acc;
      }, [])
    };

    if (editMode) {
      setAllFaqs(prevFaqs =>
        prevFaqs.map(faq =>
          faq.name === selectedCategory ? newFaq : faq
        )
      );
    } else {
      setAllFaqs(prevFaqs => [...prevFaqs, newFaq]);
    }
    handleModalClose();
    Toast.fire({ icon: 'success', title: editMode ? "FAQ Updated Locally!" : "FAQ Saved Locally!" });
  };

  const _saveOnServer = async () => {
    if (!allFaqs || allFaqs.length === 0) {
      alert("No FAQ to Save on Server");
      return;
    }

    const payload = {
      id: faq.id,
      code: faq.code,
      title: faq.title,
      isBoolean: faq.isBoolean,
      isEnabled: faq.isEnabled,
      value: JSON.stringify(allFaqs)
    };

    try {
      await _api("POST", "AppSetting/UpdateAppSetting", payload, (res) => {
        if (res.isSuccess) {
          setOriginalFaqs(allFaqs);
          Toast.fire({ icon: "success", title: res.message });
        } else {
          Toast.fire({ icon: "error", title: res.message });
        }
      });
    } catch (error) {
      console.error("Error saving FAQs:", error);
      Toast.fire({ icon: "error", title: "Failed to save FAQs" });
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditMode(false);
    setSelectedCategory('');
    setFaqItems([{ subCategory: '', qaPairs: [{ question: "", answer: "" }] }]);
  };

  const openEditModal = (faq) => {
    setSelectedCategory(faq.name);

    const items = faq.subCat.map(subCategory => ({
      subCategory: subCategory.name,
      qaPairs: subCategory.questions.map(qa => ({
        question: qa.Q,
        answer: qa.answer
      }))
    }));

    setFaqItems(items);
    setEditMode(true);
    setModalVisible(true);
  };

  if (loading) {
    return (
      <Container className="text-center my-4">
        <Spinner size="sm" color="info" />
      </Container>
    );
  }

  return (
    <>
      {hasUnsavedChanges && (
        <Alert
          message="Unsaved Changes"
          description="You have made changes to the FAQs that haven't been saved yet."
          type="warning"
          showIcon
          className="mb-3"
          style={{ borderRadius: 0 }}
        />
      )}
      <div className="d-flex justify-content-between align-items-center border p-3 rounded bg-light mb-3">
        <span className="fw-semibold mb-0 text-sm" style={{ color: "#124880" }}>FAQs Content</span>
        {rights && rights.add && (
          <button
            onClick={() => setModalVisible(true)}
            className="btn btn-outline-primary-600 radius-3 d-flex align-items-center gap-2 px-14 py-6 text-sm mb-2"
            style={{ lineHeight: '1.2' }}
          >
            Add New FAQ <Icon icon="mdi:plus-circle-outline" className="text-md" />
          </button>)}
      </div>

      {allFaqs && allFaqs.length > 0 ? (
        <Collapse
          accordion
          items={allFaqs.map((cat, i) => ({
            key: `cat-${i}`,
            label: (
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <Icon icon="mdi:book-plus-multiple-outline" className="me-2" style={{ marginRight: "2px", color: "#124880" }} />
                  <span className="me-2" style={{ color: "#124880" }}>{cat?.name ?? "Category:"}</span>
                </div>
                {rights && rights.add && (
                  <Icon icon="feather:edit" style={{ cursor: "pointer", color: "#124880" }} onClick={(e) => { e.stopPropagation(); openEditModal(cat); }} />
                )}
              </div>
            ),
            children: (
              <Collapse
                items={cat.subCat.map((sub, j) => ({
                  key: `sub-${i}-${j}`,
                  label: (
                    <div className="d-flex align-items-center">
                      <Icon icon="mdi:book-plus-outline" className="me-2" width={14} height={14} style={{ marginRight: "2px", color: "#124880" }} />
                      <span className="me-2" style={{ color: "#124880" }}>{sub?.name ?? "Sub Category"}</span>
                    </div>
                  ),
                  children: (
                    <div>
                      {sub.questions.map((q, k) => (
                        <div key={k} className="mb-3">
                          <div className="d-flex align-items-start mb-1">
                            <span className="badge bg-primary me-2 mt-1">Question</span>
                            <span className="fw-semibold me-2">{q.Q}</span>
                          </div>
                          <div className="d-flex align-items-start ms-4">
                            <span className="badge bg-success me-2 mt-1">Answer</span>
                            <span className="me-2">{q.answer}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                }))}
              />
            )
          }))}
        />
      ) : (
        <p>No FAQs found</p>
      )}

      {hasUnsavedChanges && (
        <div className="mt-3 d-flex justify-content-end">
          <button
            onClick={_saveOnServer}
            className="btn btn-outline-primary-600 px-14 py-6 text-sm radius-3"
            style={{ lineHeight: '1.2' }}
          >
            Save Changes
          </button>
        </div>
      )}
      {/* <CustomModal
        title={editMode ? "Edit FAQ" : "New FAQ"}
        show={modalVisible}
        onHide={handleModalClose}
        size="xl"
      >
        <Alert type="info" className="mb-3" description={<>FAQ will be saved locally. Don’t forget to click  <strong>Save Changes</strong> to submit to server.</>} />
        <div className="mb-0">
          <div className="mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Enter category (e.g., 'Billing', 'Account')"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            />
          </div>

          {faqItems.map((item, index) => (
            <div key={index} className="border rounded p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="flex-grow-1 me-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter sub-category (e.g., 'Payment Methods', 'Password Reset')"
                    value={item.subCategory}
                    onChange={(e) => {
                      const updated = [...faqItems];
                      updated[index].subCategory = e.target.value;
                      setFaqItems(updated);
                    }}
                  />
                </div>
                <div className="d-flex gap-2">
                  {index === faqItems.length - 1 && (
                    <Button
                      color="primary"
                      variant="text"
                      className="px-3"
                      onClick={() => {
                        const updated = [
                          ...faqItems,
                          {
                            subCategory: '',
                            qaPairs: [{ question: "", answer: "" }]
                          }
                        ];
                        setFaqItems(updated);
                      }}
                    >
                      Add
                    </Button>
                  )}
                  {faqItems.length > 1 && (
                    <Button
                      color="pink"
                      variant="text"
                      className="px-3"
                      onClick={() => {
                        const updated = faqItems.filter((_, i) => i !== index);
                        setFaqItems(updated);
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>

              {(item.qaPairs || [{ question: "", answer: "" }]).map((qa, qaIndex) => (
                <div key={qaIndex} className="mt-3">
                  <div className="d-flex align-items-start gap-0">
                    <div className="flex-grow-1 d-flex gap-3">
                      <div className="flex-grow-1">
                        <textarea
                          rows="2"
                          className="form-control"
                          placeholder="Enter your question..."
                          value={qa?.question || ""}
                          onChange={(e) => {
                            const updated = [...faqItems];
                            if (!updated[index].qaPairs) {
                              updated[index].qaPairs = [{ question: "", answer: "" }];
                            }
                            updated[index].qaPairs[qaIndex].question = e.target.value;
                            setFaqItems(updated);
                          }}
                        />
                      </div>
                      <div className="flex-grow-1">
                        <textarea
                          rows="2"
                          className="form-control"
                          placeholder="Enter the answer..."
                          value={qa?.answer || ""}
                          onChange={(e) => {
                            const updated = [...faqItems];
                            if (!updated[index].qaPairs) {
                              updated[index].qaPairs = [{ question: "", answer: "" }];
                            }
                            updated[index].qaPairs[qaIndex].answer = e.target.value;
                            setFaqItems(updated);
                          }}
                        />
                      </div>
                    </div>

                    <div className="d-flex flex-column gap-2" style={{ minWidth: '120px' }}>
                      {qaIndex === (item.qaPairs?.length || 1) - 1 && (
                        <Button
                          color="primary"
                          variant="text"
                          className="px-3"
                          onClick={() => {
                            const updated = [...faqItems];
                            if (!updated[index].qaPairs) {
                              updated[index].qaPairs = [{ question: "", answer: "" }];
                            }
                            updated[index].qaPairs.push({ question: "", answer: "" });
                            setFaqItems(updated);
                          }}
                        >
                          Add Q & A
                        </Button>
                      )}
                      {(item.qaPairs?.length || 0) > 1 && (
                        <Button
                          color="pink"
                          variant="text"
                          className="px-3"
                          onClick={() => {
                            const updated = [...faqItems];
                            if (updated[index].qaPairs) {
                              updated[index].qaPairs = updated[index].qaPairs.filter((_, i) => i !== qaIndex);
                              if (updated[index].qaPairs.length === 0) {
                                updated[index].qaPairs = [{ question: "", answer: "" }];
                              }
                            }
                            setFaqItems(updated);
                          }}
                        >
                          Remove Q & A
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}

          <div className="d-flex justify-content-end gap-3 mt-4">
            <button className="btn btn-secondary btn-sm" onClick={handleModalClose}>
              Cancel
            </button>
            <button className="btn btn-changan btn-sm" onClick={handleSave}>
              Save FAQ
            </button>
          </div>
        </div>
      </CustomModal> */}
      <CustomModal
        title={editMode ? "Edit FAQ" : "New FAQ"}
        show={modalVisible}
        onHide={handleModalClose}
        size="xl"
      >
        <Alert
          type="info"
          className="mb-3"
          description={
            <>
              FAQ will be saved locally. Don’t forget to click{" "}
              <strong>Save Changes</strong> to submit to server.
            </>
          }
        />

        <div className="container-fluid">
          {/* Category */}
          <div className="row mb-3">
            <div className="col-12">
              <input
                type="text"
                className="form-control"
                placeholder="Enter category (e.g., 'Billing', 'Account')"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              />
            </div>
          </div>

          {faqItems.map((item, index) => (
            <div key={index} className="border rounded p-3 mb-3">
              <div className="row align-items-center mb-3">
                {/* Sub-category input */}
                <div className="col-md-9 col-12 mb-2 mb-md-0">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter sub-category (e.g., 'Payment Methods', 'Password Reset')"
                    value={item.subCategory}
                    onChange={(e) => {
                      const updated = [...faqItems];
                      updated[index].subCategory = e.target.value;
                      setFaqItems(updated);
                    }}
                  />
                </div>
                {/* Add/Remove buttons */}
                <div className="col-md-3 col-12 d-flex gap-2">
                  {index === faqItems.length - 1 && (
                    <Button
                      color="primary"
                      variant="text"
                      className="w-100"
                      onClick={() => {
                        const updated = [
                          ...faqItems,
                          { subCategory: "", qaPairs: [{ question: "", answer: "" }] },
                        ];
                        setFaqItems(updated);
                      }}
                    >
                      Add
                    </Button>
                  )}
                  {faqItems.length > 1 && (
                    <Button
                      color="pink"
                      variant="text"
                      className="w-100"
                      onClick={() => {
                        const updated = faqItems.filter((_, i) => i !== index);
                        setFaqItems(updated);
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>

              {/* Q & A Pairs */}
              {(item.qaPairs || [{ question: "", answer: "" }]).map((qa, qaIndex) => (
                <div key={qaIndex} className="row g-2 mb-3">
                  {/* Question */}
                  <div className="col-md-5 col-12">
                    <textarea
                      rows="2"
                      className="form-control"
                      placeholder="Enter your question..."
                      value={qa?.question || ""}
                      onChange={(e) => {
                        const updated = [...faqItems];
                        if (!updated[index].qaPairs) {
                          updated[index].qaPairs = [{ question: "", answer: "" }];
                        }
                        updated[index].qaPairs[qaIndex].question = e.target.value;
                        setFaqItems(updated);
                      }}
                    />
                  </div>
                  {/* Answer */}
                  <div className="col-md-5 col-12">
                    <textarea
                      rows="2"
                      className="form-control"
                      placeholder="Enter the answer..."
                      value={qa?.answer || ""}
                      onChange={(e) => {
                        const updated = [...faqItems];
                        if (!updated[index].qaPairs) {
                          updated[index].qaPairs = [{ question: "", answer: "" }];
                        }
                        updated[index].qaPairs[qaIndex].answer = e.target.value;
                        setFaqItems(updated);
                      }}
                    />
                  </div>
                  {/* Action buttons */}
                  <div className="col-md-2 col-12 d-flex flex-wrap gap-2">
                    {qaIndex === (item.qaPairs?.length || 1) - 1 && (
                      <Button
                        color="primary"
                        variant="text"
                        className="w-100"
                        onClick={() => {
                          const updated = [...faqItems];
                          if (!updated[index].qaPairs) {
                            updated[index].qaPairs = [{ question: "", answer: "" }];
                          }
                          updated[index].qaPairs.push({ question: "", answer: "" });
                          setFaqItems(updated);
                        }}
                      >
                        Add Q & A
                      </Button>
                    )}
                    {(item.qaPairs?.length || 0) > 1 && (
                      <Button
                        color="pink"
                        variant="text"
                        className="w-100"
                        onClick={() => {
                          const updated = [...faqItems];
                          if (updated[index].qaPairs) {
                            updated[index].qaPairs = updated[index].qaPairs.filter(
                              (_, i) => i !== qaIndex
                            );
                            if (updated[index].qaPairs.length === 0) {
                              updated[index].qaPairs = [{ question: "", answer: "" }];
                            }
                          }
                          setFaqItems(updated);
                        }}
                      >
                        Remove Q & A
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}

          {/* Footer Buttons */}
          <div className="row mt-4">
            <div className="col-12 d-flex justify-content-end gap-2">
              <button className="btn btn-secondary btn-sm" onClick={handleModalClose}>
                Cancel
              </button>
              <button className="btn btn-changan btn-sm" onClick={handleSave}>
                Save FAQ
              </button>
            </div>
          </div>
        </div>
      </CustomModal>

    </>
  );
};

export default Faqs;